/**
 * Native replacement for Vitals' Sticky Add to Cart widget.
 * - Shows once the main product form's submit button scrolls out of view.
 * - Mirrors the main form's variant selection (price/image/select) via the
 *   theme's own PUB_SUB_EVENTS.variantChange event — read-only sync, no
 *   duplicated variant-resolution logic.
 * - Adds to cart through the SAME flow the main form uses (cart-notification
 *   or cart-drawer's own renderContents), so the confirmation UI (popup,
 *   cart icon count, etc.) behaves identically either way.
 */
class MtaStickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.variantsById = new Map();
    const variantsScript = this.querySelector('[data-mta-sticky-atc-variants]');
    if (variantsScript) {
      try {
        const variants = JSON.parse(variantsScript.textContent);
        variants.forEach((v) => this.variantsById.set(String(v.id), v));
      } catch (err) {
        // If this ever fails to parse, the widget just won't reactively
        // update price/image on variant change — it still works for adding
        // whatever variant is currently selected in its own <select>.
      }
    }

    this.select = this.querySelector('[data-mta-sticky-atc-select]');
    this.priceEl = this.querySelector('[data-mta-sticky-atc-price]');
    this.imageEl = this.querySelector('[data-mta-sticky-atc-image]');
    this.quantityInput = this.querySelector('[data-mta-sticky-atc-quantity]');
    this.submitButton = this.querySelector('[data-mta-sticky-atc-submit]');
    this.submitButtonText = this.querySelector('[data-mta-sticky-atc-submit-text]');
    this.submitButtonDefaultText = this.submitButtonText ? this.submitButtonText.textContent : '';

    this.setupVisibilityObserver();
    this.setupVariantSync();
    this.setupSubmit();
  }

  setupVisibilityObserver() {
    const submitButton = document.querySelector('[id^="ProductSubmitButton-"]');
    if (!submitButton) return;

    this.observer = new IntersectionObserver(
      (entries) => {
        const isMainButtonVisible = entries[0].isIntersecting;
        this.classList.toggle('mta-sticky-atc--visible', !isMainButtonVisible);
        this.hidden = false;
      },
      { rootMargin: '0px' }
    );
    this.observer.observe(submitButton);
  }

  setupVariantSync() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

    subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      const variant = event?.data?.variant;
      if (!variant) return;
      this.updateForVariant(variant);
    });
  }

  updateForVariant(variant) {
    if (this.select && String(this.select.value) !== String(variant.id)) {
      this.select.value = variant.id;
    }

    if (this.priceEl) {
      const parts = [];
      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        parts.push(`<s class="mta-sticky-atc__price-compare">${this.formatMoney(variant.compare_at_price)}</s>`);
      }
      parts.push(`<span class="mta-sticky-atc__price-current">${this.formatMoney(variant.price)}</span>`);
      this.priceEl.innerHTML = parts.join('');
    }

    if (this.imageEl && variant.featured_image?.src) {
      try {
        const url = new URL(variant.featured_image.src, window.location.origin);
        url.searchParams.set('width', '100');
        this.imageEl.src = url.toString();
      } catch (err) {
        // Malformed URL — leave the previous image in place rather than break.
      }
    }
  }

  formatMoney(cents) {
    // Best-effort formatting without pulling in Shopify's money filter
    // client-side; falls back to the store's currency via Intl when
    // available. Cents are already in the shop's presentment currency.
    try {
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'USD',
      }).format(cents / 100);
    } catch (err) {
      return (cents / 100).toFixed(2);
    }
  }

  setupSubmit() {
    if (!this.submitButton) return;
    this.submitButton.addEventListener('click', () => this.addToCart());
  }

  addToCart() {
    const variantId = this.select ? this.select.value : this.dataset.initialVariantId;
    if (!variantId) return;

    const quantity = this.quantityInput ? parseInt(this.quantityInput.value, 10) || 1 : 1;

    this.submitButton.setAttribute('aria-disabled', 'true');
    this.submitButton.classList.add('loading');
    if (this.submitButtonText) this.submitButtonText.textContent = 'Adding...';

    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

    const body = { id: variantId, quantity };
    if (cart && typeof cart.getSectionsToRender === 'function') {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'mta-sticky-add-to-cart',
              productVariantId: variantId,
              errors: response.errors || response.description,
              message: response.message,
            });
          }
          return;
        }

        if (cart && typeof cart.renderContents === 'function') {
          cart.setActiveElement?.(document.activeElement);
          cart.renderContents(response);
        } else if (!cart) {
          window.location = window.routes?.cart_url || '/cart';
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'mta-sticky-add-to-cart',
            productVariantId: variantId,
            cartData: response,
          });
        }
      })
      .catch(() => {})
      .finally(() => {
        this.submitButton.removeAttribute('aria-disabled');
        this.submitButton.classList.remove('loading');
        if (this.submitButtonText) this.submitButtonText.textContent = this.submitButtonDefaultText;
      });
  }
}

customElements.define('mta-sticky-atc', MtaStickyAddToCart);
